import { Button, Col, Form, Input, Row, Select, message } from 'antd'
import { useCallback, type FunctionComponent, useMemo } from 'react'
import { RegionSelect } from '~/components/RegionSelect'
import { useCreateAppstreamMutation, useUpdateAppstreamMutation } from './mutation'
import { useCourseServersQuery } from './query'

interface EditServerDrawerProps {
  appstream: any
  courseId: number
  onSuccess: () => void
}

export const AppstreamForm: FunctionComponent<EditServerDrawerProps> = ({ courseId, appstream, onSuccess }) => {
  const createMutation = useCreateAppstreamMutation()
  const updateMutation = useUpdateAppstreamMutation(appstream?.id)
  const mutation = appstream == null ? createMutation : updateMutation

  const formSubmitHandler = useCallback((values: any) => {
    mutation.mutate({ ...values, course_id: courseId }, {
      onSuccess: () => {
        onSuccess()
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message ?? 'Failed')
      }
    })
  }, [courseId, mutation, onSuccess])

  const { data: servers } = useCourseServersQuery(courseId)

  const initialValues = useMemo(() => {
    const initData = { ...appstream }
    if (appstream?.servers != null) {
      initData.server_ids = appstream.servers.map(item => item.id)
    }

    return initData
  }, [appstream])

  return (
    <Form layout="vertical" onFinish={formSubmitHandler} initialValues={initialValues}>
      <Row>
        <Col flex='1'>
          <Form.Item
            name="region_code"
            label="Location"
            rules={[{ required: true, message: 'Please select the location' }]}
          >
            <RegionSelect />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col flex='1'>
          <Form.Item
            name="stack_name"
            label="Stack Name"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input placeholder="Please enter server name" />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item
            name="server_ids"
            label="Associate Servers"
            rules={[{ required: true, message: 'Please select the servers' }]}
          >
            <Select
              showSearch
              mode="multiple"
              optionFilterProp="children"
              maxTagCount='responsive'
              filterOption={(input, option) => {
                const value = option?.value ?? ''
                return value.toString().toLowerCase().includes(input.toLowerCase())
              }}
            >
              {servers.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col flex='1'>
          <Button type="primary" htmlType="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  )
}
